import { useCallback, useState } from "react";
import { message } from "antd";
import {
  createClinicalFormApi,
  getClinicalFormApi,
  listClinicalFormsApi,
  updateClinicalFormApi,
} from "../services/clinicalForms.services";
import {
  fromApiForm,
  toClinicalForms,
  toCreateFormPayload,
  toUpdateFormPayload,
} from "../mappers/clinicalForms.mappers";

const useClinicalForms = () => {
  const [updating, setUpdating] = useState(false);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async (fn, fallbackMsg, busy) => {
    busy?.(true);
    try {
      return await fn();
    } catch (err) {
      message.error(err?.response?.data?.detail || fallbackMsg);
      throw err;
    } finally {
      busy?.(false);
    }
  }, []);

  const listForms = useCallback(
    (params) =>
      run(
        async () => {
          const { data } = await listClinicalFormsApi(params);
          const arr = Array.isArray(data)
            ? data
            : data?.results || data?.data || [];
          return toClinicalForms(arr);
        },
        "Failed to load forms",
        setLoading
      ),
    [run]
  );

  const getForm = useCallback(
    (formId) =>
      run(
        async () => {
          const { data } = await getClinicalFormApi(formId);
          return fromApiForm(data);
        },
        "Failed to load form",
        setLoading
      ),
    [run]
  );

  const createForm = useCallback(
    (input) =>
      run(
        async () => {
          const { data } = await createClinicalFormApi(
            toCreateFormPayload(input)
          );
          return fromApiForm(data);
        },
        "Failed to create form",
        setUpdating
      ),
    [run]
  );

  const updateForm = useCallback(
    (formId, input) =>
      run(
        async () => {
          const { data } = await updateClinicalFormApi(
            formId,
            toUpdateFormPayload(input)
          );
          return fromApiForm(data);
        },
        "Failed to update form",
        setUpdating
      ),
    [run]
  );

  const setFormActive = useCallback(
    (formId, isActive) => updateForm(formId, { isActive }),
    [updateForm]
  );
  const setFormRepeatable = useCallback(
    (formId, isRepeatable) => updateForm(formId, { isRepeatable }),
    [updateForm]
  );

  return {
    loading,
    updating,
    listForms,
    getForm,
    createForm,
    updateForm,
    setFormActive,
    setFormRepeatable,
  };
};

export default useClinicalForms;
